import { useI18n } from '@/composable/useI18n'
import { SensorType } from '@/interfaces/enums'
import {
  buildDoorCorrelationBackground,
  type IDoorCorrelationBackground,
} from '@/utils/dashboard/door-correlation-background'
import type { BuildRegisterChartOptionConfig } from '@/utils/components/register-chart'
import {
  buildRegisterChartAxisRange,
  buildRegisterChartSeries,
  useRegisterChart,
  type IRegisterChartSensorData,
  type IRegisterChartSeries,
} from '@/utils/components/register-chart'
import {
  buildRegisterChartContinuousInputs,
  buildRegisterChartDoorLanes,
  partitionRegisterSensors,
} from '@/utils/register-chart-series'
import type { EChartsOption } from 'echarts'
import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'

export type RegisterChartPresentationMode = 'single' | 'dual-axis' | 'multi-grid'

/** Configuração pronta para `buildChartOption` (sem knobs de apresentação). */
export type RegisterChartMultiGridConfig = Pick<
  BuildRegisterChartOptionConfig,
  'energySeries' | 'temperatureSeries' | 'doors' | 'range' | 'energyUnit' | 'temperatureUnit'
>

function isDoorSensorData(data: IRegisterChartSensorData | null | undefined): boolean {
  return data?.sensor?.sensor_type === SensorType.DOOR_OPEN
}

export function useRegisterChartPresentation(
  primary: MaybeRefOrGetter<IRegisterChartSensorData | null | undefined>,
  secondary: MaybeRefOrGetter<IRegisterChartSensorData | null | undefined>,
  additional: MaybeRefOrGetter<IRegisterChartSensorData[]> = () => [],
): {
  mode: ComputedRef<RegisterChartPresentationMode>
  chartSeries: ComputedRef<IRegisterChartSeries[]>
  xAxisMin: ComputedRef<number | undefined>
  xAxisMax: ComputedRef<number | undefined>
  primaryUnitDisplay: ComputedRef<string>
  secondaryUnitDisplay: ComputedRef<string>
  unitDisplay: ComputedRef<string>
  doorOpenLegendLabel: ComputedRef<string>
  yAxisOption: ComputedRef<EChartsOption['yAxis']>
  hasDualAxis: ComputedRef<boolean>
  isMultiGrid: ComputedRef<boolean>
  multiGridConfig: ComputedRef<RegisterChartMultiGridConfig | null>
  doorCorrelationBackground: ComputedRef<IDoorCorrelationBackground | null>
} {
  const { t } = useI18n()
  const { chartOptions } = useRegisterChart(primary)

  const allSensors = computed(() => {
    const sources = [
      toValue(primary),
      toValue(secondary),
      ...(toValue(additional) ?? []),
    ]
    // Deduplica por sensor (id ou, na falta dele, nome) para o MESMO sensor não
    // vir por dois caminhos (secondary + lista adicional), o que duplicaria a lane.
    const seen = new Set<string>()
    return sources.filter((data) => {
      const key = data?.sensor?.id != null ? String(data.sensor.id) : data?.sensor?.name
      if (!key) return true
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  })

  const partition = computed(() => partitionRegisterSensors(allSensors.value))

  const hasDoor = computed(() => partition.value.doors.length > 0)
  const hasContinuous = computed(
    () => partition.value.energy.length + partition.value.temperature.length > 0,
  )
  const isPrimaryDoor = computed(() => isDoorSensorData(toValue(primary)))

  const primaryIsTemperature = computed(
    () => toValue(primary)?.sensor?.sensor_type === SensorType.TEMPERATURE,
  )

  // Timeline (grid de portas) apenas com MAIS de uma porta correlacionada —
  // com uma única porta usa-se o comportamento de faixas no gráfico principal.
  const isMultiGrid = computed(
    () => hasDoor.value && hasContinuous.value && partition.value.doors.length > 1,
  )

  // Dual-eixo apenas com sensores contínuos de DOIS tipos (ex.: energia +
  // temperatura). Sensores do MESMO tipo (ex.: energia + energia) ficam juntos
  // no eixo principal (single), comparáveis entre si.
  const hasDualAxis = computed(
    () =>
      partition.value.energy.length > 0 &&
      partition.value.temperature.length > 0 &&
      !isMultiGrid.value &&
      !isPrimaryDoor.value,
  )

  const mode = computed((): RegisterChartPresentationMode => {
    if (isMultiGrid.value) return 'multi-grid'
    if (hasDualAxis.value) return 'dual-axis'
    return 'single'
  })

  const buildChartSeriesAtAxis = (
    sensorData: IRegisterChartSensorData,
    yAxisIndex: number,
  ): IRegisterChartSeries[] =>
    buildRegisterChartSeries(sensorData, {
      namePrefix: sensorData.sensor?.name,
      yAxisIndex,
    })

  /** Séries contínuas do gráfico principal (single/dual-axis), incluindo TODOS os correlacionados. */
  const mainChartSeries = computed((): IRegisterChartSeries[] => {
    const p = partition.value

    // Porta como primário: as contínuas (de qualquer tipo) vão no eixo principal.
    if (isPrimaryDoor.value) {
      return [
        ...p.energy.flatMap((sd) => buildChartSeriesAtAxis(sd, 0)),
        ...p.temperature.flatMap((sd) => buildChartSeriesAtAxis(sd, 0)),
      ]
    }

    // Sem eixos distintos (mesmo tipo): todas as séries no eixo 0.
    if (!hasDualAxis.value) {
      return [
        ...p.energy.flatMap((sd) => buildChartSeriesAtAxis(sd, 0)),
        ...p.temperature.flatMap((sd) => buildChartSeriesAtAxis(sd, 0)),
      ]
    }

    // Dual: tipo do primário no eixo 0; o outro tipo no eixo 1.
    const energyAxis = primaryIsTemperature.value ? 1 : 0
    const temperatureAxis = primaryIsTemperature.value ? 0 : 1
    return [
      ...p.energy.flatMap((sd) => buildChartSeriesAtAxis(sd, energyAxis)),
      ...p.temperature.flatMap((sd) => buildChartSeriesAtAxis(sd, temperatureAxis)),
    ]
  })

  const multiGridChartSeries = computed((): IRegisterChartSeries[] => {
    if (!isMultiGrid.value) return []
    return [
      ...partition.value.energy.flatMap((sensorData) =>
        buildRegisterChartSeries(sensorData, {
          namePrefix: sensorData.sensor?.name,
          yAxisIndex: 0,
        }),
      ),
      ...partition.value.temperature.flatMap((sensorData) =>
        buildRegisterChartSeries(sensorData, {
          namePrefix: sensorData.sensor?.name,
          yAxisIndex: 1,
        }),
      ),
    ]
  })

  const chartSeries = computed((): IRegisterChartSeries[] => {
    if (isMultiGrid.value) return multiGridChartSeries.value
    return mainChartSeries.value
  })

  const globalRange = computed(() => buildRegisterChartAxisRange(...allSensors.value))

  const doorCorrelationBackground = computed<IDoorCorrelationBackground | null>(() => {
    if (isMultiGrid.value) return null
    const doors = partition.value.doors
    if (doors.length !== 1) return null
    return buildDoorCorrelationBackground(doors[0], globalRange.value.min, globalRange.value.max)
  })

  const multiGridConfig = computed((): RegisterChartMultiGridConfig | null => {
    if (!isMultiGrid.value) return null

    const { energy, temperature, doors } = partition.value
    const range = buildRegisterChartAxisRange(...allSensors.value)

    return {
      energySeries: energy.flatMap((sensorData) =>
        buildRegisterChartContinuousInputs(sensorData, { namePrefix: sensorData.sensor?.name }),
      ),
      temperatureSeries: temperature.flatMap((sensorData) =>
        buildRegisterChartContinuousInputs(sensorData, { namePrefix: sensorData.sensor?.name }),
      ),
      doors: buildRegisterChartDoorLanes(doors, range),
      range,
      energyUnit: energy[0]?.sensor?.unit_display || 'A',
      temperatureUnit: temperature[0]?.sensor?.unit_display || '°C',
    }
  })

  const primaryUnitDisplay = computed(() => {
    if (isMultiGrid.value) {
      return (
        partition.value.energy[0]?.sensor?.unit_display ??
        partition.value.temperature[0]?.sensor?.unit_display ??
        (chartOptions.value as { unitDisplay?: string })?.unitDisplay ??
        'A'
      )
    }
    if (isPrimaryDoor.value) {
      return (
        toValue(secondary)?.sensor?.unit_display ??
        (chartOptions.value as { unitDisplay?: string })?.unitDisplay ??
        'A'
      )
    }
    return (
      toValue(primary)?.sensor?.unit_display ??
      (chartOptions.value as { unitDisplay?: string })?.unitDisplay ??
      'A'
    )
  })

  const secondaryUnitDisplay = computed(() => {
    // No dual-axis, o eixo 1 recebe o tipo contínuo OPOSTO ao primário.
    if (hasDualAxis.value) {
      return primaryIsTemperature.value
        ? partition.value.energy[0]?.sensor?.unit_display ?? 'A'
        : partition.value.temperature[0]?.sensor?.unit_display ?? '°C'
    }
    return toValue(secondary)?.sensor?.unit_display ?? ''
  })

  const unitDisplay = computed(() => primaryUnitDisplay.value)

  const doorOpenLegendLabel = computed(() => t('custom_dashboards.view.time_series.door_open'))

  const xAxisMin = computed(() => globalRange.value.min)
  const xAxisMax = computed(() => globalRange.value.max)

  const yAxisOption = computed((): EChartsOption['yAxis'] => {
    if (isMultiGrid.value) {
      const primaryUnit = primaryUnitDisplay.value
      const secondaryUnit = partition.value.temperature[0]?.sensor?.unit_display ?? '°C'

      return [
        {
          type: 'value',
          name: primaryUnit,
          nameLocation: 'end',
          nameGap: 8,
          position: 'left',
          axisLabel: {
            fontSize: 10,
            formatter: (value: number) => `${value?.toFixed(2)} ${primaryUnit}`,
          },
          splitLine: { lineStyle: { type: 'dashed', opacity: 0.4 } },
        },
        {
          type: 'value',
          name: secondaryUnit,
          nameLocation: 'end',
          nameGap: 8,
          position: 'right',
          axisLabel: {
            fontSize: 10,
            formatter: (value: number) => `${value?.toFixed(2)} ${secondaryUnit}`,
          },
          splitLine: { show: false },
        },
      ]
    }

    if (!hasDualAxis.value) {
      return {
        type: 'value',
        name: unitDisplay.value,
        nameLocation: 'end',
        nameGap: 8,
        axisLabel: {
          fontSize: 10,
          formatter: (value: number) => `${value?.toFixed(2)} ${unitDisplay.value}`,
        },
        splitLine: { lineStyle: { type: 'dashed', opacity: 0.4 } },
      }
    }

    const primaryUnit = primaryUnitDisplay.value
    const secondaryUnit = secondaryUnitDisplay.value

    return [
      {
        type: 'value',
        name: primaryUnit,
        nameLocation: 'end',
        nameGap: 8,
        position: 'left',
        axisLabel: {
          fontSize: 10,
          formatter: (value: number) => `${value?.toFixed(2)} ${primaryUnit}`,
        },
        splitLine: { lineStyle: { type: 'dashed', opacity: 0.4 } },
      },
      {
        type: 'value',
        name: secondaryUnit,
        nameLocation: 'end',
        nameGap: 8,
        position: 'right',
        axisLabel: {
          fontSize: 10,
          formatter: (value: number) => `${value?.toFixed(2)} ${secondaryUnit}`,
        },
        splitLine: { show: false },
      },
    ]
  })

  return {
    mode,
    chartSeries,
    xAxisMin,
    xAxisMax,
    primaryUnitDisplay,
    secondaryUnitDisplay,
    unitDisplay,
    doorOpenLegendLabel,
    yAxisOption,
    hasDualAxis,
    isMultiGrid,
    multiGridConfig,
    doorCorrelationBackground,
  }
}